import React from 'react';
import styled from 'styled-components';
import LoadingSpinner from '../atoms/loading';
import I10n from '../features/i10n';
import PropTypes from 'prop-types';
import SavedConfigurationItem from './saved_configuration_item';
import MsxIcons from '../features/msx-icons';
import { PRIMARY_COLOR } from '../color-constants';
import { LICENSE_MODELS } from '../constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;
`;

const ScrollbarsWrapper = styled.div`
  flex: 1 1 0px;
  margin-top: 5px;
`;

const InputLoadConfigurationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
  padding: 5px 0px;
  max-width: 33%;
  @media (max-width: 800px) {
    max-width: unset;
  }
`;

const OpenConfigurationInput = styled.input`
  flex: 1 1 0px;
  color: #333333;
  font-size: 11px;
  line-height: 12px;
  padding: 10px;
  border: 1px solid #e9e9e9;
  ::placeholder {
    color: #a0a4a8;
  }
`;

const OpenConfigurationInputButton = styled.button`
  background-color: #626669;
  color: white;
  padding: 4px 11px;
  outline: none;
  border: none;
  cursor: pointer;
  ${props => props.disabled ? `opacity: 0.5; cursor: not-allowed;` : `
    :hover {
      background-color: #333333;
    }
  `}
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const SavedConfigurationListWrapper = styled.div`
  display: flex;
  flex: 1 1 0px;
  flex-wrap: wrap;
  margin-right: -15px;
  @media (max-width: 800px) {
    margin-right: -10px;
    flex-direction: column;
  }
`;

const CardWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;
  margin-right: 10px;
  min-width: 200px;
  width: calc(33% - 10px);
  @media (max-width: 800px) {
    flex: unset;
    min-width: unset;
    width: auto;
  }
`;

const ViewMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${PRIMARY_COLOR};
  color: var(--se-secondary, ${PRIMARY_COLOR});
  cursor: pointer;
`;

const ViewMoreLabel = styled.span`
  font-size: 14px;
`;

const OpenIconWrapper = styled.div`
  margin-right: 5px;
  transition: transform 0.3s;
  transform: ${props => props.showFull ? 'rotate(180deg)' : 'rotate(0deg)'};
  width: 32px;
  height: 32px;
  box-sizing: content-box;
  display: flex;
  justify-content: center;
  align-items: center;
`;


const configurationsToShowTypes = {
  all: 'ALL',
  plc: 'PLC',
  pac: 'PAC'
}

class SavedConfigurationsList extends React.Component {

  static propTypes = {
    configurations: PropTypes.array,
    isLoading: PropTypes.bool,
    onConfigurationRestore: PropTypes.func,
    onRename: PropTypes.func,
    onDelete: PropTypes.func,
    onExport: PropTypes.func,
    onAddToMyProducts: PropTypes.func,
    onAddToList: PropTypes.func,
    onSendMail: PropTypes.func,
  }

  constructor(props) {
    super(props);
    this.state = {
      name: '',
      showFullConfigurations: false,
      configurationsToShow: configurationsToShowTypes.all
    }
  }

  componentDidMount = () => {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  updateStateIfMounted = (stateToSet) => {
    if (this._isMounted) {
      this.setState(stateToSet);
    }
  }

  renderSavedConfigurations = candidateConfigurations => {
    if (candidateConfigurations.length) {
      let configurationsToShow = candidateConfigurations;
      if (!this.state.showFullConfigurations) {
        configurationsToShow = candidateConfigurations.slice(0, 6);
      }

      return configurationsToShow.map((configuration, idx) => {
        const isSubscriptionMode = configuration.licenseModel === LICENSE_MODELS.SUBSCRIPTION;

        return (
          <CardWrapper id='CardWrapper' key={configuration.id} lessThan3Configurations={configurationsToShow.length < 3}>
            <SavedConfigurationItem
              id={`config-saved-${idx}`}
              configuration={configuration}
              isSubscriptionMode={isSubscriptionMode}
              onConfigurationRestore={this.props.onConfigurationRestore}
              onExport={this.props.onExport}
              onDuplicate={this.props.onDuplicate}
              onSendMail={this.props.onSendMail}
              onAddToMyProducts={this.props.onAddToMyProducts}
              onAddToList={this.props.onAddToList}
              onRename={this.props.onRename}
              onDelete={this.props.onDelete}
              launchFromWorkspace={this.props.launchFromWorkspace}
              unifiedCheckoutAvailable={this.props.unifiedCheckoutAvailable}
              isWorkspaceAvailable={this.props.isWorkspaceAvailable}
              primaryColor={this.props.primaryColor}
              secondaryColor={this.props.secondaryColor}
              active={this.props.active}
            >
            </SavedConfigurationItem>
          </CardWrapper>
        );
      });
    } else {
      return <span>{I10n.get('screen.start_configuration_screen.load.from_saved.empty')}</span>;
    }
  }

  handleInputChange = (event) => {
    const value = event.target.value;
    const name = event.target.name;
    if (this.state[name] !== value) {
      this.setState({ [name]: value });
    }
  }

  render = () => {
    let candidateConfigurations = this.props.configurations;

    return (
      <Container id='saved-configurations-list-Container'>
        <InputLoadConfigurationWrapper>
          <OpenConfigurationInput
            placeholder={I10n.get('screen.start_configuration_screen.load.from_id.placeholder')}
            value={this.state.name}
            name="name"
            onChange={this.handleInputChange}
          >
          </OpenConfigurationInput>
          <OpenConfigurationInputButton disabled={this.state.name?.length === 0} onClick={() => {
            this.props.onConfigurationRestore(this.state.name.trim());
          }}>
            {I10n.get('screen.start_configuration_screen.load.from_id.confirm')}
          </OpenConfigurationInputButton>
        </InputLoadConfigurationWrapper>
        <ScrollbarsWrapper>
          {this.props.isLoading ?
            <LoadingWrapper>
              <LoadingSpinner></LoadingSpinner>
            </LoadingWrapper>
            :
            <SavedConfigurationListWrapper>
              {this.renderSavedConfigurations(candidateConfigurations)}
            </SavedConfigurationListWrapper>
          }
        </ScrollbarsWrapper>
        {(candidateConfigurations.length > 6) &&
          <ViewMoreWrapper onClick={() => { this.updateStateIfMounted({ showFullConfigurations: !this.state.showFullConfigurations }) }}>
            <ViewMoreLabel>
              {this.state.showFullConfigurations ? I10n.get('screen.start_configuration_screen.view_less') : I10n.getAndReplace('screen.start_configuration_screen.view_more', this.props.configurations.length - 6)}
            </ViewMoreLabel>
            <OpenIconWrapper showFull={this.state.showFullConfigurations}>
              <MsxIcons name="down-arrow" size="small"></MsxIcons>
            </OpenIconWrapper>
          </ViewMoreWrapper>}
      </Container>
    );
  }
}

export default SavedConfigurationsList;
